import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "../components/site/Seo";
import { Container } from "../components/site/Ui";
import { Logo } from "../components/site/Navbar";
import { api, errMessage } from "../lib/api";

const field =
  "w-full rounded-xl border border-slate-300 px-4 py-3 text-base text-slate-900 outline-none transition-colors focus:border-[#F95738] focus:ring-2 focus:ring-[#F95738]/20";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setSent(true);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Reset your password" description="Request a password reset link for your PostMint account." path="/forgot-password" />
      <Container className="flex min-h-[70vh] items-center justify-center py-16">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-7 soft-shadow sm:p-9">
          <Logo />
          <h1 className="mt-6 text-3xl font-bold text-slate-900">Forgot your password?</h1>
          <p className="mt-2 text-sm text-slate-600">
            Enter your email address and we'll send you a link to set a new password.
          </p>

          {sent ? (
            <p data-testid="forgot-success" className="mt-6 rounded-xl bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
              If an account exists for that email, a reset link has been sent.
            </p>
          ) : (
            <form onSubmit={submit} noValidate className="mt-7 space-y-5" data-testid="forgot-form">
              <div>
                <label htmlFor="forgot-email" className="text-sm font-semibold text-slate-800">Email</label>
                <input id="forgot-email" type="email" data-testid="forgot-email-input" className={`mt-2 ${field}`} value={email} onChange={(e) => setEmail(e.target.value)} />
                {error && <p data-testid="forgot-email-error" className="mt-1.5 text-sm text-red-600">{error}</p>}
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit-button"
                className="w-full rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#E0482B] disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send Reset Link"}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-600">
            <Link to="/login" data-testid="forgot-back-login-link" className="font-semibold text-[#F95738] hover:underline">
              Back to login
            </Link>
          </p>
        </div>
      </Container>
    </>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const n = {};
    if (!token) n.form = "This reset link is missing its token.";
    if (form.password.length < 8) n.password = "Please use at least 8 characters.";
    if (form.password !== form.confirm) n.confirm = "Passwords do not match.";
    setErrors(n);
    if (Object.keys(n).length) return;

    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: form.password });
      toast.success(data.message);
      navigate("/login", { replace: true });
    } catch (err) {
      setErrors({ form: errMessage(err) });
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Set a new password" description="Set a new password for your PostMint account." path="/reset-password" />
      <Container className="flex min-h-[70vh] items-center justify-center py-16">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-7 soft-shadow sm:p-9">
          <Logo />
          <h1 className="mt-6 text-3xl font-bold text-slate-900">Set a new password.</h1>
          <form onSubmit={submit} noValidate className="mt-7 space-y-5" data-testid="reset-form">
            {[
              ["password", "New Password"],
              ["confirm", "Confirm Password"],
            ].map(([key, label]) => (
              <div key={key}>
                <label htmlFor={`reset-${key}`} className="text-sm font-semibold text-slate-800">{label}</label>
                <input
                  id={`reset-${key}`}
                  type="password"
                  autoComplete="new-password"
                  data-testid={`reset-${key}-input`}
                  className={`mt-2 ${field}`}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
                {errors[key] && <p className="mt-1.5 text-sm text-red-600">{errors[key]}</p>}
              </div>
            ))}
            {errors.form && (
              <p data-testid="reset-form-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                {errors.form}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="w-full rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#E0482B] disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save New Password"}
            </button>
          </form>
        </div>
      </Container>
    </>
  );
}
